import { FormSubmission } from '@hotwired/turbo'
import { Turbo } from '@hotwired/turbo-rails'

import { stopFormSubmission, extractCredentials } from 'controllers/login_controller'

import {
  generateAndUploadDeviceKey,
  loadDeviceKeyPairForDeviceToken,
  TokenType,
} from '../../encryption/encryption'

import { LoginService, LoginDelegate } from '../../services/authentication/loginService'
import { CSRFToken } from '../../util/csrfToken'

import AuthenticationTokenBaseController from './authentication_token_base_controller'

/**
 * BridgeLoginController
 *
 * Responsible for handling the login form submission and coordinating with LoginService
 * for authentication and device key management.
 *
 * This controller:
 * - Implements the same logic as the LoginController up until the point where the user
 *   has successfully authenticated and the device key is loaded by deriving from email
 *   and password
 * - Creates an authentication token for authenticating the native app later
 * - Fetches the device token from the native app and onboards a new DeviceKey using that
 *   device token
 *
 * **Important:** This controller should be used instead of the LoginController when using the native app.
 */
export default class BridgeLoginController
  extends AuthenticationTokenBaseController<HTMLFormElement>
  implements LoginDelegate
{
  static component = 'bridge--login'

  private loginService: LoginService = new LoginService(this)

  static targets = ['submitButton']

  declare submitButtonTarget: HTMLButtonElement
  declare hasSubmitButtonTarget: boolean
  private deviceToken?: string

  /**
   * Handles the form submission start event
   * Intercepts the form submission and delegates to LoginService
   */
  async onSubmitStart(event: CustomEvent<{ formSubmission: FormSubmission }>) {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
    }
    const formData = stopFormSubmission(event)
    const credentials = extractCredentials(formData)

    try {
      await this.loginService.call(credentials)
    } catch (e) {
      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.disabled = false
      }
      throw e
    }
  }

  // LoginDelegate implementation
  async onAuthenticationError(): Promise<void> {
    this.dispatch('onAuthenticationError')
  }

  async onAuthenticationComplete(): Promise<void> {
    await this.createAuthenticationToken()
  }

  async onAuthenticationAndKeyLoadingComplete(): Promise<void> {}

  async onDeviceKeyLoadingError(): Promise<void> {
    Turbo.visit('/encryption/unlocking_error', { action: 'replace' })
  }

  /**
   * This is called once the password-based device master key is loaded.
   *
   * We can now see to onboard the app-specific device token.
   */
  async onDeviceKeyLoaded(): Promise<void> {
    if (!this.deviceToken) {
      console.error('No deviceToken available for authentication')
      return
    }

    const deviceKey = await loadDeviceKeyPairForDeviceToken(this.deviceToken)
    if (!deviceKey) {
      await generateAndUploadDeviceKey(this.deviceToken, TokenType.DeviceToken)
    }
  }

  /**
   * Creates a new authentication token for authenticating the native app later
   */
  private async createAuthenticationToken() {
    try {
      const authToken = await this.fetchAuthenticationToken()

      const tokenResponse = await this.setAuthToken(authToken)
      this.deviceToken = tokenResponse?.deviceToken
    } catch (error) {
      console.error('Failed to create or send authentication token:', error)
      throw error
    }
  }

  /**
   * Creates a new authentication token on the server
   */
  private async fetchAuthenticationToken(): Promise<string> {
    const response = await fetch('/authentication_tokens', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': CSRFToken(),
      },
      body: JSON.stringify({ device_name: 'Native App' }),
    })

    if (!response.ok) {
      throw new Error(`Failed to create token: ${response.statusText}`)
    }

    const data = await response.json()
    return data.token
  }
}
